import { type CSSProperties, useEffect } from 'react';

import { useInView } from 'react-intersection-observer';

import { useMeasureElement } from '../../hooks/useMeasureElement';
import useScrollbarWidth from '../../hooks/useScrollbarWidth';

export type UseStickyProps = {
    /**
     * Callback function to be called when the sticky state changes
     */
    onChange?: (isSticky: boolean) => void;
    /**
     * If `true`, the sticky behavior will be disabled
     */
    disabled?: boolean;
    /**
     * Custom styles to apply to the sticky element
     */
    style?: CSSProperties;
};

/**
 * A hook that provides sticky behavior for an element.
 * It uses the Intersection Observer API to determine if the element is in view,
 * and applies fixed positioning when it is not.
 */
export function useSticky({ onChange, disabled, style }: UseStickyProps = {}) {
    const scrollbarWidth = useScrollbarWidth();
    const { ref: intersectionRef, inView } = useInView({
        threshold: 1,
        initialInView: true,
        skip: disabled
    });
    const isSticky = !disabled && !inView;

    useEffect(() => {
        if (onChange) {
            onChange(isSticky);
        }
    }, [isSticky, onChange]);

    // track element height for the placeholder
    const [elementRef, { height: elementHeight }] = useMeasureElement<HTMLDivElement>({
        disabled
    });

    // calculate styles for the sticky element and placeholder
    const targetStyles: CSSProperties = isSticky
        ? {
              width: `calc(100% - ${scrollbarWidth}px)`,
              position: 'fixed',
              zIndex: 999,
              top: 0,
              left: 0,
              ...style
          }
        : {};
    const placeholderStyles: CSSProperties = isSticky
        ? {
              height: isSticky ? elementHeight : 0
          }
        : {};

    return {
        intersectionRef,
        elementRef,
        isSticky,
        targetStyles,
        placeholderStyles,
        disabled
    };
}
